import React, { useEffect, useMemo } from 'react'
import { useBreakpointValue } from '@chakra-ui/react'
import TimeDesktop from './desktop'
import TimeMobile from './mobile'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { IRedux } from '@/app/[locale]/_store/types'
import dayjs from 'dayjs'
import { useTranslations } from 'next-intl'
import { useQuery } from '@tanstack/react-query'
import getShipperById from '@/app/[locale]/_service/shipper.service'
import { commonActions } from '@/app/[locale]/_store/common/common.slice'

import isBetween from 'dayjs/plugin/isBetween'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import { branchService } from '@/app/[locale]/_service/branch.service'
import { checkoutActions } from '@/app/[locale]/_store/checkout/checkout.slice'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

dayjs.extend(isBetween)
dayjs.extend(isSameOrBefore)

interface TimeProps {
  open?: boolean
  onOpen?: () => void
  onClose?: () => void
  showTrigger?: boolean
}

export default function Time({
  open,
  onOpen,
  onClose,
  showTrigger = true,
}: TimeProps) {
  const { common, settings, session, checkout } = useSelector(
    (state: IRedux) => ({
      common: state.common,
      settings: state.settings,
      session: state.session,
      checkout: state.checkout,
    }),
    shallowEqual
  )
  const { order_type } = session
  const { future_time } = checkout

  const isDesktop = useBreakpointValue(
    { base: false, lg: true },
    { ssr: false }
  )

  const dispatch = useDispatch()
  const t = useTranslations()

  const { data: scheduleData } = useQuery({
    queryKey: ['branch_working_time', common?.branch?.id],
    queryFn: () =>
      branchService
        .getWorkingHours({
          object_id: common?.branch?.id,
          object_type: 'branch_working_time',
        })
        .then((res) => res?.data),
    enabled: Boolean(common?.branch?.id),
  })

  useEffect(() => {
    if (session?.shipper) {
      dispatch(commonActions.futureTime(session?.shipper?.future_order_time))
    }
  }, [session?.shipper])

  const interval = useMemo(() => {
    if (
      common?.branch?.future_self_pickup_order_time ||
      common?.branch?.future_delivery_order_time
    ) {
      if (
        (order_type?.type === 'self-pickup' || order_type?.type === 'hall') &&
        +common?.branch?.future_self_pickup_order_time !== 0
      ) {
        return parseInt(common?.branch?.future_self_pickup_order_time)
      }
      if (
        order_type?.type === 'hall' &&
        +common?.branch?.future_self_pickup_order_time !== 0
      ) {
        return parseInt(common?.branch?.future_self_pickup_order_time)
      }
      if (
        order_type?.type === 'delivery' &&
        +common?.branch?.future_delivery_order_time !== 0
      ) {
        return parseInt(common?.branch?.future_delivery_order_time)
      }
    }

    return common?.future_order_time ? parseInt(common?.future_order_time) : 0
  }, [common?.branch, order_type?.type, common?.future_order_time])

  const day = useMemo(() => {
    if (!future_time) return null // Handle missing data

    if (dayjs(future_time).isSame(dayjs(), 'day')) {
      return t('today')
    } else if (dayjs(future_time).isSame(dayjs().add(1, 'day'), 'day')) {
      return t('tomorrow')
    } else {
      return dayjs(future_time).format('DD MMMM')
    }
  }, [future_time])

  const deliveryMinutes = useMemo(
    () =>
      order_type?.type === 'delivery'
        ? Number(common?.branch?.delivery_time) || 0
        : 0,
    [order_type?.type, common?.branch?.delivery_time]
  )

  const leadMinutes = useMemo(() => {
    if (order_type?.type === 'delivery') {
      return deliveryMinutes || 60
    }
    return interval || 60
  }, [order_type?.type, deliveryMinutes, interval])

  const formattedTime = useMemo(() => {
    if (!future_time) {
      if (settings?.data?.data?.is_pre_order_time === false) {
        return t('when_ready')
      }
      return `${t('when_ready')} (${t('in_x_min', {
        x: leadMinutes || 60,
      })})`
    }

    const futureTime = dayjs(future_time)
    const startTime = futureTime.format('HH:mm')

    return `${day}, ${startTime}`
  }, [
    future_time,
    day,
    t,
    leadMinutes,
    settings?.data?.data?.is_pre_order_time,
  ])

  const onConfirm = (time: string) => {
    if (time) {
      dispatch(checkoutActions.changeFutureTime(time))
    } else {
      dispatch(checkoutActions.changeFutureTime(null))
    }
  }

  if (isDesktop) {
    return (
      <TimeDesktop
        open={open}
        onOpen={onOpen}
        onClose={onClose}
        interval={interval}
        onConfirm={onConfirm}
        showTrigger={showTrigger}
        phone={session?.shipper?.phone?.[0]}
        scheduleData={scheduleData}
        formattedTime={formattedTime}
      />
    )
  }
  return (
    <TimeMobile
      open={open}
      onOpen={onOpen}
      onClose={onClose}
      interval={interval}
      onConfirm={onConfirm}
      showTrigger={showTrigger}
      phone={session?.shipper?.phone?.[0]}
      scheduleData={scheduleData}
      formattedTime={formattedTime}
    />
  )
}
